import { useRef, useState } from "react";
import _ from "lodash";
import { useI18n } from "Hooks";
import {
  setValue,
  getIsHolidayEventType,
  addTimeToDate,
} from "../modules/Utils";

import { CALENDAR_TYPES } from "MyCalendar/modules/MyCalendarModule";

import {
  formatDate,
  isStartDateTimeAfterEndDateTime,
  getDateMomentObject,
  getIncrementedDate,
} from "Utils/DateTimeHelpers";
import { useMyCalendarContext } from "MyCalendar/context";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getRepeatEventOptions } from "../../../modules/Selector";
import { STEPS_COUNT } from "MyCalendar/modules/Utils";

const MIN_SLOT_HEIGHT = 5;

// Q. why 50 ms timeout?
// A. keeping timeout 50 ms to balance between responsiveness and performance
// less timeout is causing issue with some browsers(eg:firefox) where scroll end event is not detected properly
function onScrollEnd(callback, parentElem = document, timeout = 50) {
  let timer;

  const rbcTimeContentContainerElement = parentElem.querySelector(
    ".rbc-time-content"
  );
  function handler() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      rbcTimeContentContainerElement?.removeEventListener("scroll", handler);
      callback();
    }, timeout);
  }

  rbcTimeContentContainerElement?.addEventListener("scroll", handler);
}

/**
 * Scrolls a time slot into view and updates the dropdown position accordingly.
 * If the slot is not in view, it performs a smooth scroll and waits for the scroll
 * to complete before updating the dropdown position. Handles visibility toggling
 * to prevent flickering during scroll animations.
 *
 * @param {Object} params - The parameters object
 * @param {boolean} params.isInView - Whether the slot is currently in view
 * @param {HTMLElement} params.startNode - The DOM node representing the start time slot
 * @param {HTMLElement} params.endNode - The DOM node representing the end time slot
 * @param {HTMLElement} params.selectedSlot - The currently selected slot element
 * @param {string} params.startSlotId - The class name/ID of the start slot for querying
 * @param {string} params.endSlotId - The class name/ID of the end slot for querying
 * @param {Function} params.setSlotDropdown - State setter function to update slot dropdown position
 * @param {HTMLElement} [params.parentElement=document] - The parent element to use for scroll handling
 */
const scrollSlotIntoView = ({
  isInView,
  startNode,
  endNode,
  selectedSlot,
  startSlotId,
  endSlotId,
  setSlotDropdown,
  parentElement = document,
}) => {
  // add transition to selected slot for smooth movement
  if (selectedSlot) {
    selectedSlot.style.transition = `top 0.6s ease,
    left 0.6s ease,
    right 0.6s ease,
    bottom 0.6s ease`;
  }

  // scroll the slot into view if not already in view
  if (!isInView) {
    startNode?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });

    onScrollEnd(() => {
      // get the x,y coordinates of the slot relative to the viewport
      const startSlot = document.getElementsByClassName(startSlotId);

      const endSlot = document.getElementsByClassName(endSlotId);
      const startNode = startSlot[0];
      const endNode = endSlot?.[0];

      const { x: startSlotLeftPos, y: startSlotTopPos } =
        startNode?.getBoundingClientRect() || {};
      const { y: endSlotY = 0 } = endNode?.getBoundingClientRect() || {};

      setSlotDropdown(prevState => ({
        ...prevState,
        top: startSlotTopPos,
        left: startSlotLeftPos,
        height: Math.max(endSlotY - startSlotTopPos, MIN_SLOT_HEIGHT),
      }));
    }, parentElement);
  } else {
    const { x: startSlotLeftPos, y: startSlotTopPos } =
      startNode?.getBoundingClientRect() || {};
    const { y: endSlotY = 0 } = endNode?.getBoundingClientRect() || {};

    setSlotDropdown(prevState => ({
      ...prevState,
      top: startSlotTopPos,
      left: startSlotLeftPos,
      height: Math.max(endSlotY - startSlotTopPos, MIN_SLOT_HEIGHT),
    }));
  }
};

/**
 * Checks if an element is in view relative to its parent element
 * @param {HTMLElement} element - The element to check
 * @param {HTMLElement} parent - The parent element to check against
 * @returns {boolean} - True if element is in view, false otherwise
 */
const isElementInView = (element, parent) => {
  if (!element || !parent) return false;

  const elementRect = element.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();

  return (
    elementRect.top >= parentRect.top &&
    elementRect.left >= parentRect.left &&
    elementRect.bottom <= parentRect.bottom &&
    elementRect.right <= parentRect.right
  );
};

// Rounds down the eventStartDate to the nearest STEPS_COUNT minutes
const getRoundedDate = eventStartDate => {
  const roundedDate = getDateMomentObject(eventStartDate).clone();
  const minutes = roundedDate.minutes();
  const roundedMinutes = Math.floor(minutes / STEPS_COUNT) * STEPS_COUNT;
  roundedDate.minutes(roundedMinutes).seconds(0).milliseconds(0);
  return roundedDate;
};

/**
 * Updates the slot dropdown position for the global calendar daily view.
 * Rounds the event start date to the nearest STEPS_COUNT minutes, locates the corresponding
 * time slot element in the DOM, scrolls it into view if necessary, and updates
 * the dropdown position with the slot's y coordinate.
 *
 * @param {Object} params - The parameters object
 * @param {moment.Moment|Date|string} params.eventStartDate - The start date of the event
 * @param {Function} params.setSlotDropdown - State setter function to update slot dropdown position
 */
function globalCalendarDailyViewSlotPositionUpdateHandler({
  eventStartDate,
  setSlotDropdown,
  eventEndDate,
}) {
  // Round eventStartDate to nearest STEPS_COUNT minutes
  const roundedStartDate = getRoundedDate(eventStartDate);

  const formattedRoundedStartDate = formatDate({
    date: roundedStartDate.utc(),
    format: "YYYY-MM-DD-HH-mm-ss-SSS",
  });

  const roundedEndDate = getRoundedDate(eventEndDate);

  const formattedRoundedEndDate = formatDate({
    date: roundedEndDate.utc(),
    format: "YYYY-MM-DD-HH-mm-ss-SSS",
  });

  const startSlotId = `dailyView-${formattedRoundedStartDate}-`;
  const startSlot = document.getElementsByClassName(startSlotId);

  const endSlotId = `dailyView-${formattedRoundedEndDate}-`;
  const endSlot = document.getElementsByClassName(endSlotId);
  if (!_.isEmpty(startSlot)) {
    // Check if the slot is in view of the daily calendar body
    const parentElement = document.getElementById(
      "globalCalendarDailyViewBody"
    );
    const isInView = isElementInView(startSlot[0], parentElement);
    const startNode = startSlot[0];
    const endNode = endSlot?.[0];
    const selectedSlot = document.getElementById("globalCalendarEventSlot");
    scrollSlotIntoView({
      isInView,
      startNode,
      endNode,
      selectedSlot,
      startSlotId,
      endSlotId,
      setSlotDropdown,
      parentElement,
    });
  }
}

/**
 * Updates the slot dropdown position for the master calendar weekly view.
 * Rounds the event start date to the nearest 5 minutes, locates the corresponding
 * time slot element in the DOM, scrolls it into view if necessary, and updates
 * the dropdown position with the slot's x and y coordinates.
 *
 * @param {Object} params - The parameters object
 * @param {moment.Moment|Date|string} params.eventStartDate - The start date of the event
 * @param {Function} params.setSlotDropdown - State setter function to update slot dropdown position
 */
function masterCalendarWeeklyViewSlotPositionUpdateHandler({
  eventStartDate,
  setSlotDropdown,
  eventEndDate,
}) {
  // Round eventStartDate to nearest 5 minutes
  const roundedStartDate = getRoundedDate(eventStartDate);

  const roundedEndDate = getRoundedDate(eventEndDate);

  const startSlotId = roundedStartDate.toISOString();
  const endSlotId = roundedEndDate.toISOString();

  const startSlot = document.getElementsByClassName(startSlotId);

  const endSlot = document.getElementsByClassName(endSlotId);
  if (!_.isEmpty(startSlot)) {
    const startNode = startSlot[0];

    const endNode = endSlot?.[0];

    const isInView = isElementInView(
      startNode,
      document.getElementsByClassName("rbc-time-content")?.[0]
    );

    const selectedSlot = document.getElementById("masterCalendarEventSlot");

    scrollSlotIntoView({
      isInView,
      startNode,
      endNode,
      selectedSlot,
      startSlotId,
      endSlotId,
      setSlotDropdown,
    });
  }
}

const updateSlotPosition = ({
  calendarType,
  eventStartDate,
  eventEndDate,
  setSlotDropdown,
}) => {
  if (calendarType === CALENDAR_TYPES.MASTER_CALENDAR) {
    masterCalendarWeeklyViewSlotPositionUpdateHandler({
      eventStartDate,
      eventEndDate,
      setSlotDropdown,
    });
  } else {
    globalCalendarDailyViewSlotPositionUpdateHandler({
      eventStartDate,
      setSlotDropdown,
      eventEndDate,
    });
  }
};

const useDateTime = props => {
  const {
    eventModalV2,
    updateEventModalV2,
    dateTimeContainerClass = "",
    curriculumProgramId,
    currentAcademicYearId,
    isSelectedSlotVisible,
  } = props;
  const _setEventStartDate = setValue("startDate", updateEventModalV2);
  const _setEventEndDate = setValue("endDate", updateEventModalV2);
  const setIsAllDayEvent = setValue("isAllDayEvent", updateEventModalV2);
  const setReminder = setValue("reminders", updateEventModalV2);
  const setIsRepeatEvent = setValue("isRepeatEvent", updateEventModalV2);
  const { setSlotDropdown, calendarType } = useMyCalendarContext();
  const isAllDayCheckedRef = useRef(eventModalV2?.isAllDayEvent);

  const {
    isAllDayEvent,
    startDate = getDateMomentObject(),
    endDate = getDateMomentObject(),
    eventUID,
    isRepeatEvent,
  } = eventModalV2 || {};

  const setEventStartDate = (eventStartDate, isAllDayChecked) => {
    // Calculate the time difference between endDate and startDate
    const timeDiff = getDateMomentObject(endDate).diff(
      getDateMomentObject(startDate)
    );
    // Adjust end date to maintain the same duration when start date changes
    let adjustedEndDate = getDateMomentObject(eventStartDate)
      .clone()
      .add(timeDiff, "milliseconds");

    // Check if adjustedEndDate exceeds to a different day than the start date
    if (
      formatDate({
        date: getDateMomentObject(eventStartDate),
        format: "YYYY-MM-DD",
      }) !== formatDate({ date: adjustedEndDate, format: "YYYY-MM-DD" })
    ) {
      // If it exceeds, set to end of day of the start date
      adjustedEndDate = getDateMomentObject(eventStartDate)
        .clone()
        .endOf("day");
    }

    _setEventStartDate(eventStartDate);
    _setEventEndDate(adjustedEndDate);
    if (!isSelectedSlotVisible || isAllDayEvent || isAllDayChecked) return;
    updateSlotPosition({
      calendarType,
      eventStartDate: eventStartDate,
      eventEndDate: adjustedEndDate,
      setSlotDropdown,
    });
  };

  const setEventEndDate = (latestEndDate, isAllDayChecked) => {
    _setEventEndDate(latestEndDate);
    if (!isSelectedSlotVisible || isAllDayEvent || isAllDayChecked) return;

    // startDate may contains stale value here
    // Adjust the event start date to align with the new end date's day for time based events date will remain same
    const eventStartDate = getDateMomentObject(startDate).clone().set({
      year: latestEndDate.year(),
      month: latestEndDate.month(),
      date: latestEndDate.date(),
    });

    updateSlotPosition({
      calendarType,
      eventStartDate: eventStartDate,
      eventEndDate: latestEndDate,
      setSlotDropdown,
    });
  };

  const setRepeatEventSettings = setValue(
    "repeatEventSettings",
    updateEventModalV2
  );

  const { t } = useI18n();

  const [savedTimeBasedDates, setSavedTimeBasedDates] = useState({});
  const [savedAllDayDates, setSavedAllDayDates] = useState({});

  const dateFormat = isAllDayEvent
    ? DATE_TIME_CONSTANTS.DD_MMM_YYYY
    : DATE_TIME_CONSTANTS.DD_MMM_YYYY_HH_mm;
  const getFormattedDate = date => {
    const formattedDate = formatDate({
      date,
      format: dateFormat,
    });
    return formattedDate;
  };

  const setStartDateHandler = selectedStartDate => {
    if (isAllDayEvent) {
      setEventStartDate(selectedStartDate);
      return;
    }
    if (selectedStartDate) {
      const currentDuration = getDateMomentObject(endDate).diff(
        getDateMomentObject(startDate)
      );
      const newEndDate = selectedStartDate
        .clone()
        .add(currentDuration, "milliseconds");
      if (
        formatDate({
          date: selectedStartDate,
          format: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
        }) ===
        formatDate({
          date: newEndDate,
          format: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
        })
      ) {
        _setEventEndDate(newEndDate);
      } else {
        // If adding duration crosses to next day, set end date to end of same day
        _setEventEndDate(selectedStartDate.clone().endOf("day"));
      }
    }
    setEventStartDate(selectedStartDate);
  };

  const fullDayEventHandler = event => {
    // no need to change slot position as it was already set when time based dates were initially set i.e.
    // why we are using _setEventEndDate _setEventStartDate to update redux state only for now
    if (getIsHolidayEventType(eventUID)) return;

    const isFullDayEvent = event?.target?.checked;

    if (isFullDayEvent) {
      // Save the current time based dates to restore later
      setSavedTimeBasedDates({ startDate, endDate });

      // for all-day event, restore previously saved all-day dates if any update the dates in redux state no need to update slot position for now
      if (!_.isEmpty(savedAllDayDates)) {
        _setEventStartDate(savedAllDayDates.startDate);
        _setEventEndDate(savedAllDayDates.endDate);
      }
      // When converting to an all-day event, remove reminders as timed based reminders don't apply to all-day events
      setReminder([]);
    } else {
      // Save the current all day dates to restore later
      setSavedAllDayDates({ startDate, endDate });

      // When converting from an all-day event to timebased event, restore previously saved time based dates if any update,
      // update dates in redux state no need to update slot position for now
      if (!_.isEmpty(savedTimeBasedDates)) {
        _setEventStartDate(savedTimeBasedDates.startDate);
        _setEventEndDate(savedTimeBasedDates.endDate);
      } else if (!_.isEmpty(startDate)) {
        _setEventStartDate(
          addTimeToDate(getDateMomentObject(startDate), getDateMomentObject())
        );
        _setEventEndDate(
          addTimeToDate(
            getDateMomentObject(startDate),
            getIncrementedDate({
              date: getDateMomentObject(),
              key: "minutes",
              incrementBy: 30,
            })
          )
        );
      }
    }
    setIsAllDayEvent(isFullDayEvent);
  };

  const repeatEventHandler = event => {
    setIsRepeatEvent(event?.target?.checked);
    const repeatEventOptions = getRepeatEventOptions({
      date: startDate,
      t,
      curriculumProgramId,
      currentAcademicYearId,
    });
    setRepeatEventSettings(repeatEventOptions[1]?.value);
  };

  const setEndDateHandler = value => {
    if (
      isStartDateTimeAfterEndDateTime({
        startDateTime: startDate,
        endDateTime: endDate,
        format: dateFormat,
      }) &&
      value
    ) {
      setEventStartDate(value);
    }
    setEventEndDate(value);
  };
  return {
    ...props,
    setEndDateHandler,
    setStartDateHandler,
    fullDayEventHandler,
    isAllDayEvent,
    startDate,
    endDate,
    dateFormat,
    dateTimeContainerClass,
    setIsAllDayEvent,
    getFormattedDate,
    setEventStartDate,
    setEventEndDate,
    isStartDateTimeAfterEndDateTime,
    eventUID,
    repeatEventHandler,
    isRepeatEvent,
  };
};

export default useDateTime;
